import {
  Listing,
  Attribute,
  PropertyType,
  Province,
  District,
  City,
} from "@/lib/types"; // Import all necessary types
import { useListingWizardContext } from "@/context/ListingWizardContext";
import { Button } from "@/components/ui/button";
import { Edit2 } from "lucide-react";
import { FormStep, ListingFormValues } from "../filters/FilterSchema";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

// IMPORT THE NEW HOOKS
import { useAttributes } from "@/hooks/useAttributes";
import { usePropertyTypes } from "@/hooks/usePropertyTypes"; // Assuming this exists
import { useProvinces } from "@/hooks/useProvinces"; // New hook
import { useDistricts } from "@/hooks/useDistricts"; // New hook
import { useCities } from "@/hooks/useCities"; // New hook

interface ReviewStepProps {
  formValues: ListingFormValues;
}

const ReviewStep = ({ formValues }: ReviewStepProps) => {
  const { setCurrentStep, draftListing } = useListingWizardContext();

  // Fetch all lookup data
  const { data: attributesData, isLoading: attributesLoading } =
    useAttributes();
  const { data: propertyTypesData, isLoading: propertyTypesLoading } =
    usePropertyTypes();
  const { data: provincesData, isLoading: provincesLoading } = useProvinces();
  const { data: districtsData, isLoading: districtsLoading } = useDistricts();
  const { data: citiesData, isLoading: citiesLoading } = useCities();

  // Create lookup maps
  const attributeMap = new Map<number, Attribute>(
    attributesData?.map((attr) => [attr.id, attr]) || []
  );
  const propertyTypeMap = new Map<number, PropertyType>(
    propertyTypesData?.map((pt) => [pt.id, pt]) || []
  );
  const provinceMap = new Map<number, Province>(
    provincesData?.map((p) => [p.id, p]) || []
  );
  const districtMap = new Map<number, District>(
    districtsData?.map((d) => [d.id, d]) || []
  );
  const cityMap = new Map<number, City>(
    citiesData?.map((c) => [c.id, c]) || []
  );

  // Handle loading state for all lookup data
  if (
    attributesLoading ||
    propertyTypesLoading ||
    provincesLoading ||
    districtsLoading ||
    citiesLoading
  ) {
    return <div>Loading review details...</div>;
  }

  // Navigate to a specific step for editing
  const handleEditStep = (step: FormStep) => {
    setCurrentStep(step);
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: formValues.currency || "LKR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get display names using IDs from formValues
  const selectedPropertyTypeName =
    propertyTypeMap.get(formValues.propertyType)?.display_name || "N/A";
  const selectedProvinceName = formValues.location.provinceId
    ? provinceMap.get(formValues.location.provinceId)?.name
    : "N/A";
  const selectedDistrictName = formValues.location.districtId
    ? districtMap.get(formValues.location.districtId)?.name
    : "N/A";
  const selectedCityName = formValues.location.city_id
    ? cityMap.get(formValues.location.city_id)?.name
    : "N/A";

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Review Your Listing</h3>
        <p className="text-sm text-gray-500">
          Please review all information before submitting your listing.
        </p>
      </div>

      {/* Property Information */}
      <div className="border rounded-md p-4">
        <div className="flex justify-between items-center">
          <h4 className="text-base font-medium">Property Information</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleEditStep(FormStep.PropertyInformation)}
          >
            <Edit2 className="h-4 w-4 mr-2" /> Edit
          </Button>
        </div>
        <Separator className="my-3" />
        <div className="grid gap-2">
          {/* Use selectedPropertyTypeName from formValues */}
          <div className="grid grid-cols-2">
            <span className="text-sm text-gray-500">Property Type</span>
            <span className="text-sm font-medium">
              {selectedPropertyTypeName}
            </span>
          </div>
          <div className="grid grid-cols-2">
            <span className="text-sm text-gray-500">Listing Type</span>
            <span className="text-sm font-medium">
              {formValues.listingType}
            </span>
          </div>
          <div className="grid grid-cols-2">
            <span className="text-sm text-gray-500">Price</span>
            <span className="text-sm font-medium">
              {formatCurrency(formValues.price)}{" "}
              {formValues.pricePeriod !== "One Time"
                ? `(${formValues.pricePeriod})`
                : ""}
            </span>
          </div>
        </div>
      </div>

      {/* Property Details */}
      <div className="border rounded-md p-4">
        <div className="flex justify-between items-center">
          <h4 className="text-base font-medium">Property Details</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleEditStep(FormStep.PropertyDetails)}
          >
            <Edit2 className="h-4 w-4 mr-2" /> Edit
          </Button>
        </div>
        <Separator className="my-3" />
        <div className="grid gap-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <span className="text-sm text-gray-500">Floor Size</span>
              <p className="text-sm font-medium">
                {formValues.floorSizeSqFt} sq ft
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Rooms</span>
              <p className="text-sm font-medium">{formValues.rooms}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Bathrooms</span>
              <p className="text-sm font-medium">{formValues.bathrooms}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Parking</span>
              <p className="text-sm font-medium">{formValues.parkings}</p>
            </div>
          </div>

          <div className="mt-2">
            <span className="text-sm text-gray-500">Attributes</span>
            <div className="flex flex-wrap gap-2 mt-1">
              {formValues.attributes && formValues.attributes.length > 0 ? (
                formValues.attributes.map((attributeId) => (
                  <Badge key={attributeId} variant="secondary">
                    {/* Use attributeMap to get the name */}
                    {attributeMap.get(attributeId)?.name ||
                      `Unknown Attribute (${attributeId})`}
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-gray-400">
                  No attributes specified
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="border rounded-md p-4">
        <div className="flex justify-between items-center">
          <h4 className="text-base font-medium">Location</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleEditStep(FormStep.Location)}
          >
            <Edit2 className="h-4 w-4 mr-2" /> Edit
          </Button>
        </div>
        <Separator className="my-3" />
        <div className="grid gap-2">
          {/* Use IDs and lookup maps for Province, District, City */}
          {formValues.location.provinceId && (
            <div className="grid grid-cols-2">
              <span className="text-sm text-gray-500">Province</span>
              <span className="text-sm font-medium">
                {selectedProvinceName}
              </span>
            </div>
          )}
          {formValues.location.districtId && (
            <div className="grid grid-cols-2">
              <span className="text-sm text-gray-500">District</span>
              <span className="text-sm font-medium">
                {selectedDistrictName}
              </span>
            </div>
          )}
          {formValues.location.city_id && (
            <div className="grid grid-cols-2">
              <span className="text-sm text-gray-500">City</span>
              <span className="text-sm font-medium">{selectedCityName}</span>
            </div>
          )}
          {/* <div className="grid grid-cols-2">
            <span className="text-sm text-gray-500">Address</span>
            <span className="text-sm font-medium">
              {formValues.location.address}
            </span>
          </div> */}
        </div>
      </div>

      {/* Media */}
      <div className="border rounded-md p-4">
        <div className="flex justify-between items-center">
          <h4 className="text-base font-medium">Media</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleEditStep(FormStep.Media)}
          >
            <Edit2 className="h-4 w-4 mr-2" /> Edit
          </Button>
        </div>
        <Separator className="my-3" />

        {/* Images */}
        <div className="mb-4">
          <span className="text-sm text-gray-500">Images</span>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
            {draftListing?.images && draftListing.images.length > 0 ? (
              draftListing.images.map((image) => {
                console.log("[ReviewStep] Image URL:", image.url); // Add this log
                return (
                  <div key={image.id} className="relative">
                    <img
                      src={image.url}
                      alt="Property"
                      className={`h-24 w-full object-cover rounded-md ${
                        image.is_primary ? "ring-2 ring-blue-500" : ""
                      }`}
                    />
                    {image.is_primary === true && (
                      <span className="absolute bottom-0 left-0 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-tr-md">
                        Primary
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <span className="text-sm text-gray-400 col-span-4">
                No images uploaded
              </span>
            )}
          </div>
        </div>

        {/* Video URL */}
        <div className="grid grid-cols-2">
          <span className="text-sm text-gray-500">Video URL</span>
          <span className="text-sm font-medium">
            {formValues.videoUrl ? (
              <a
                href={formValues.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                {formValues.videoUrl}
              </a>
            ) : (
              <span className="text-gray-400">No video provided</span>
            )}
          </span>
        </div>
      </div>

      {/* Contact */}
      <div className="border rounded-md p-4">
        <div className="flex justify-between items-center">
          <h4 className="text-base font-medium">Contact Information</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleEditStep(FormStep.Contact)}
          >
            <Edit2 className="h-4 w-4 mr-2" /> Edit
          </Button>
        </div>
        <Separator className="my-3" />
        <div className="grid gap-2">
          {formValues.contactDetails?.name && (
            <div className="grid grid-cols-2">
              <span className="text-sm text-gray-500">Name</span>
              <span className="text-sm font-medium">
                {formValues.contactDetails?.name || ""}
              </span>
            </div>
          )}
          {formValues.contactDetails?.phone && (
            <div className="grid grid-cols-2">
              <span className="text-sm text-gray-500">Phone</span>
              <span className="text-sm font-medium">
                {formValues.contactDetails?.phone || ""}
              </span>
            </div>
          )}
          {formValues.contactDetails?.email && (
            <div className="grid grid-cols-2">
              <span className="text-sm text-gray-500">Email</span>
              <span className="text-sm font-medium">
                {formValues.contactDetails?.email || ""}
              </span>
            </div>
          )}
          <div className="grid grid-cols-2">
            <span className="text-sm text-gray-500">WhatsApp</span>
            <span className="text-sm font-medium">
              {formValues.contactDetails?.whatsapp ? "Yes" : "No"}
            </span>
          </div>
          <div className="grid grid-cols-2">
            <span className="text-sm text-gray-500">Botim</span>
            <span className="text-sm font-medium">
              {formValues.contactDetails?.botim ? "Yes" : "No"}
            </span>
          </div>
        </div>
      </div>

      {/* Title & Description */}
      <div className="border rounded-md p-4">
        <div className="flex justify-between items-center">
          <h4 className="text-base font-medium">Title & Description</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleEditStep(FormStep.TitleDescription)}
          >
            <Edit2 className="h-4 w-4 mr-2" /> Edit
          </Button>
        </div>
        <Separator className="my-3" />
        <div className="space-y-3">
          <div>
            <h5 className="text-sm text-gray-500">Title</h5>
            <p className="font-medium">{formValues.title}</p>
          </div>
          {formValues.cplk_ref && (
            <div>
              <h5 className="text-sm text-gray-500">CPLK Reference Code</h5>
              <p className="font-medium">{formValues.cplk_ref}</p>
            </div>
          )}
          <div>
            <h5 className="text-sm text-gray-500">Description</h5>
            <div
              className="prose max-w-none text-sm whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: formValues.description }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;
